// Art for the game world (plan v0.6, section B): enemies and bosses, NPC portraits,
// resources, backgrounds, buildings and zones.
//
// Files (copied from assets/art/webp by `pnpm icons:sync`):
//   /assets/art/{folder}/{name}_{size}.webp
// Portraits, icons, backgrounds and buildings have a transparent background; zones are
// full 16:9 scenes with a darker band at the bottom for text.

import { STACKS } from './content/resources';
import { enemyShowId, type EnemyShowId } from './content/enemies';
import { NAMED, type NamedId } from './named';
import { ZONES } from './content/zones';
import type { BodyType } from './characters';
import { BACKGROUNDS, type BackgroundId, type EnemyType, type StackKind, type ZoneId } from './types';
import { EVENT_BY_ID, EXPEDITION_EVENTS } from './events';
import { STORY_CHAINS } from './story';

export const ART_BASE_PATH = '/assets/art';

/** The 12 NPC portraits: 6 men and 6 women, the gender is in the file name. */
export const NPC_PORTRAITS = ['npc_01_m', 'npc_02_f', 'npc_03_f', 'npc_04_m', 'npc_05_f', 'npc_06_f', 'npc_07_m', 'npc_08_f', 'npc_09_m', 'npc_10_m', 'npc_11_f', 'npc_12_m'] as const;

export const BOSS_ART = { brute: 'boss_brute', warlord: 'boss_raider_warlord', hive: 'boss_hive' } as const;
export type BossId = keyof typeof BOSS_ART;

const ENEMY_ART: Partial<Record<EnemyType, string>> = { infected: 'enemy_infected', raider: 'enemy_raider', mutant: 'enemy_mutant', drone: 'enemy_drone' };

/**
 * Package 4 (owner, 7. 10. 2026): a raider by its faction and a drone by its WARDEN unit, as the zone names them (part 0's
 * table): Tollmen on Route 9, Downtown and Mercy General, the Foundry in Calder Steelworks, Fort Halden, The Burn and the
 * Vault; the drones by the same table. Without a zone the old pictures (enemy_raider, enemy_drone). Only the picture:
 * the fight goes by type as before.
 */
const SHOW_ART: Partial<Record<EnemyShowId, string>> = { tollmen: 'enemy_raider_tollmen', foundry: 'enemy_raider_foundry', patrol: 'enemy_drone_patrol', enforcer: 'enemy_drone_enforcer', scanner: 'enemy_drone_scanner' };

/** Package 4, part D: the named enemies' portraits, also in 512 (the Collection book's Named page). */
export const NAMED_ART: Record<NamedId, string> = Object.fromEntries(Object.values(NAMED).map((n) => [n.id, n.art])) as Record<NamedId, string>;

/** Package 4, part I: the story's people (Doc Mara; Elias Crane and his drone Pidge), NPC portraits like the Trader's. */
export const CHARACTER_ART = { doc_mara: 'npc_doc_mara', elias_crane: 'npc_elias_crane' } as const;

export const BUILDING_ART_IDS = ['workshop', 'storage', 'medical_tent', 'barricade'] as const;

/** Krug 5 (K11.6): the Trader, Tobias: his portrait (not in the random NPC pool) and his truck on the Camp. */
export const TRADER_ART = { portrait: 'trader_portrait', building: 'building_trader' } as const;

/**
 * Package 4, part K: stacks whose pictures the owner is still making (the list in docs/OPIS_IGRE.md); a grey frame until
 * then, and `icons:check` does not look for them. Empty since 8. 10. 2026 (the five special stacks and the seven meals
 * have their pictures).
 */
export const ART_PENDING = {
  resources: [] as string[],
  // 11. 10. 2026 (owner, BACKLOG #33): the crew chest, the crew outfit's icon and the Crew tab's header (the owner's strips
  // A: 2160 x 360 and 780 x 240) came the same day.
  cosmetics: [] as string[],
  crew: [] as string[],
  crewHeader: [] as string[],
  // 11. 10. 2026 (owner, docs/thepit.md): The Pit's pictures come from another chat (docs/notes/thepit.md, "Slike"); until
  // then a placeholder. Take a name out of its list when its files are in assets/art. The arena, the chest and the belt
  // came the same evening; the style icons and the league emblems are SVG drawings since deploy8 (`PIT_DRAWINGS`).
  pit: [] as string[],
  pitHeader: [] as string[],
  // 11. 10. 2026 (owner): the Market's scene came from another chat the same evening (Asset Bible, "The Pit i tržište").
  marketHeader: [] as string[],
};

/**
 * 11. 10. 2026 (owner, BACKLOG #33): the crews' art, made in another chat (list in docs/notes/crews.md, "Slike"). While a
 * name is in `ART_PENDING` the game shows a placeholder and `icons:check` does not look for it.
 */
export const CREW_ART = { outfit: 'cosmetic_crew_outfit', chest: 'crew_chest', header: 'crew_header' } as const;

/**
 * 11. 10. 2026 (owner, docs/thepit.md): The Pit's art. deploy8: the style icons and the league emblems are SVG drawings
 * (`PIT_DRAWINGS`, pitArt.ts), so only the chest, the belt and the header are pictures. The three style icons (the picker at 48 px, the fight history at
 * 24–32), the two league emblems (next to the rating and in the league table, 24–64), the weekly chest (the reward list at
 * 40; only 256 and 128), the champion's belt (the Pit Champion title in the Collection at 64) and the screen's header
 * (2160 x 360 on a desktop, 780 x 240 on a phone; shown 120 px high, 180 on a desktop, like the Crew tab's).
 */
export const PIT_ART = {
  styles: { aggressive: 'pit_style_aggressive', balanced: 'pit_style_balanced', defensive: 'pit_style_defensive' },
  leagues: { 1: 'pit_league_1', 2: 'pit_league_2' } as Record<number, string>,
  chest: 'pit_chest',
  belt: 'pit_belt',
  header: 'pit_header',
} as const;

/** Pictures with fewer sizes than their folder (`folder/name`: its sizes): the Pit's chest has no 64. */
export const ART_ONLY_SIZES: Record<string, readonly number[]> = { 'pit/pit_chest': [256, 128] };

/**
 * 11. 10. 2026 (owner): the Market screen's header, 2160×360 on a desktop and 780×240 on a phone
 * (`market_header_2160.webp`, `market_header_780.webp`). Pending: once the files are in, take it out of `ART_PENDING`.
 */
export const MARKET_ART = { header: 'market_header' } as const;

/** Every folder with its sizes (largest first) and file names. */
export const ART = {
  enemies: { sizes: [256, 128], names: [...Object.values(ENEMY_ART), ...Object.values(BOSS_ART), ...Object.values(SHOW_ART), ...Object.values(NAMED_ART)] as string[] },
  npc: { sizes: [256, 128], names: [...NPC_PORTRAITS, TRADER_ART.portrait, ...Object.values(CHARACTER_ART)] as string[] },
  resources: { sizes: [256, 128, 64], names: Object.keys(STACKS).filter((k) => !ART_PENDING.resources.includes(k)) },
  backgrounds: { sizes: [512, 256], names: BACKGROUNDS.map((b) => `bg_${b}`) },
  buildings: { sizes: [512, 256], names: [...BUILDING_ART_IDS.map((b) => `building_${b}`), TRADER_ART.building] },
  zones: { sizes: [1280, 640], names: Object.keys(ZONES) },
  // 8. 10. 2026 (owner): the story chains' cosmetics, an icon next to the chain's reward (on the survivor later, BACKLOG).
  cosmetics: { sizes: [256, 128, 64], names: [...Object.values(STORY_CHAINS).map((c) => c.reward.cosmeticArt), CREW_ART.outfit].filter((n) => !ART_PENDING.cosmetics.includes(n)) as string[] },
  // 11. 10. 2026 (BACKLOG #33): the crew chest, and the Crew tab's header (2160 px wide for the desktop, 780 for a phone).
  crew: { sizes: [256, 128], names: [CREW_ART.chest].filter((n) => !ART_PENDING.crew.includes(n)) as string[] },
  crewHeader: { sizes: [2160, 780], names: [CREW_ART.header].filter((n) => !ART_PENDING.crewHeader.includes(n)) as string[] },
  // 11. 10. 2026 (owner, docs/thepit.md): The Pit's icons and emblems, and its header (none while they wait in `ART_PENDING`).
  pit: {
    sizes: [256, 128, 64],
    // deploy8: the style icons and the league emblems are drawings (`PIT_DRAWINGS`), not pictures.
    names: [PIT_ART.chest, PIT_ART.belt].filter((n) => !ART_PENDING.pit.includes(n)) as string[],
  },
  pitHeader: { sizes: [2160, 780], names: [PIT_ART.header].filter((n) => !ART_PENDING.pitHeader.includes(n)) as string[] },
  // 11. 10. 2026 (owner): the Market's header (pending: no file is asked for until it leaves `ART_PENDING`).
  marketHeader: { sizes: [2160, 780], names: [MARKET_ART.header].filter((n) => !ART_PENDING.marketHeader.includes(n)) as string[] },
} as const satisfies Record<string, { sizes: readonly number[]; names: string[] }>;

export type ArtFolder = keyof typeof ART;
export type ArtSize<F extends ArtFolder> = (typeof ART)[F]['sizes'][number];

export function artUrl<F extends ArtFolder>(folder: F, name: string, size: ArtSize<F>): string {
  return `${ART_BASE_PATH}/${folder}/${name}_${size}.webp`;
}

/** Whether the art set has this picture (anything else falls back to the old icon). */
export function hasArt(folder: ArtFolder, name: string | null | undefined): name is string {
  return !!name && ART[folder].names.includes(name);
}

/** Package 4, part D: pictures that also come in 512 (the named enemies on the Named page, about 250 px wide). */
export const ART_LARGE = { enemies: { size: 512, names: Object.values(NAMED_ART) } } as const;

/** A picture's 512 file (only the ones in ART_LARGE have it). */
export function artUrlLarge(folder: keyof typeof ART_LARGE, name: string): string | null {
  return (ART_LARGE[folder].names as readonly string[]).includes(name) ? `${ART_BASE_PATH}/${folder}/${name}_${ART_LARGE[folder].size}.webp` : null;
}

/** Every file the client expects, relative to /assets/art (for `pnpm icons:check`). */
export const ART_FILES: string[] = [
  ...(Object.keys(ART) as ArtFolder[]).flatMap((f) => ART[f].names.flatMap((n) => (ART_ONLY_SIZES[`${f}/${n}`] ?? ART[f].sizes).map((s) => `${f}/${n}_${s}.webp`))),
  ...(Object.keys(ART_LARGE) as (keyof typeof ART_LARGE)[]).flatMap((f) => ART_LARGE[f].names.map((n) => `${f}/${n}_${ART_LARGE[f].size}.webp`)),
];

/** Distinct sources: one picture per name, in every size (62 in total; K11 added the Trader's portrait and truck). */
export const ART_SOURCE_COUNT = (Object.keys(ART) as ArtFolder[]).reduce((a, f) => a + ART[f].names.length, 0);

/** An enemy's portrait: a raider or a drone by its zone (package 4), a named enemy its own; no zone: the type's. */
export function enemyArt(enemy: EnemyType | string | null | undefined, zone?: ZoneId | null, named?: NamedId | null): string | null {
  if (named && NAMED_ART[named]) return NAMED_ART[named];
  if (!enemy) return null;
  if (zone && (enemy === 'raider' || enemy === 'drone')) return SHOW_ART[enemyShowId(enemy, zone)] ?? ENEMY_ART[enemy] ?? null;
  return ENEMY_ART[enemy as EnemyType] || null;
}

export function bossArt(boss: string | null | undefined): string | null {
  return (boss && BOSS_ART[boss as BossId]) || null;
}

export function resourceArt(kind: StackKind): string | null {
  return hasArt('resources', kind) ? kind : null;
}

export function backgroundArt(bg: BackgroundId): string {
  return `bg_${bg}`;
}

export function buildingArt(id: string): string | null {
  const name = `building_${id}`;
  return hasArt('buildings', name) ? name : null;
}

export function zoneArt(zone: ZoneId | string): string | null {
  return hasArt('zones', zone) ? zone : null;
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export type NpcGender = 'm' | 'f';

export function genderOf(bodyType: BodyType): NpcGender {
  return bodyType === 'female' ? 'f' : 'm';
}

/**
 * NPC portrait (B2): `portraits of the same gender[hash(id) % 6]`. The same NPC (same id)
 * always gets the same face: in the encounter, in the report and in the horde.
 */
export function npcPortrait(id: string, gender: NpcGender): string {
  const pool = NPC_PORTRAITS.filter((p) => p.endsWith(`_${gender}`));
  return pool[hash(id) % pool.length];
}

/**
 * Portrait of an opponent: NPC survivors (and their ghosts) get an NPC portrait keyed by
 * their ghost id; real players and their ghosts keep their own survivor picture (null).
 */
export function oppPortrait(opp: { kind?: string; ghostId?: string | null; name: string; bodyType?: BodyType }): string | null {
  if (opp.kind !== 'npc') return null;
  return npcPortrait(opp.ghostId || opp.name, genderOf(opp.bodyType ?? 'male'));
}

/** Strangers in choice events (B2): a man, a woman, or anyone; `option` limits the face to one choice. */
const EVENT_STRANGERS: Record<string, { gender: NpcGender | null; option?: string }> = {
  wounded_stranger: { gender: 'm' },
  barricaded_doctor: { gender: 'f' },
  smoking_cabin: { gender: null, option: 'knock' },
  // Krug 5 (K14.5): the new events with a stranger in them.
  wary_traveler: { gender: null },
  last_residents: { gender: null },
  // Package 4, part J: the new events' strangers (two survivors over a can of peaches; the wounded man at triage).
  peaches: { gender: null },
  triage: { gender: 'm' },
};

/** Events that start with an enemy in plain sight (a direct fight option): their portrait (B1). */
const EVENT_ENEMIES: Record<string, EnemyType> = {
  wolf_den: 'mutant',
  raider_toll: 'raider',
  raider_tracks: 'raider',
  // Package 4, part J: the Tollmen and the Foundry, WARDEN's drones, the Infected and Subject 0001 in the new events.
  route9_toll: 'raider',
  price_board: 'raider',
  exemption_card: 'raider',
  under_the_pumps: 'raider',
  stuck_truck: 'raider',
  recruiter: 'raider',
  union_meeting: 'raider',
  tribute: 'raider',
  checkpoint_scan: 'drone',
  parking_violation: 'drone',
  customer_satisfaction: 'drone',
  signature_required: 'infected',
  open_house: 'infected',
  campfire_story: 'infected',
  subject_0001: 'mutant',
};

/** Package 4, part J: events that start with a named enemy in plain sight (its portrait). */
const EVENT_NAMED: Record<string, NamedId> = { free_samples: 'salesman', the_first_gate: 'the_first', ashwalker_event: 'ashwalker' };

/**
 * Picture for a choice event window: a stranger's face (picked by `hash(expeditionId + eventId)`,
 * so it stays the same while the window is open and on a reload), or the enemy of a fight event.
 */
/**
 * Krug 5 (K14.5): the options that can end in a fight (right away, or when a roll fails), with the
 * enemy: the event window shows its portrait next to the choice. Read from the event file.
 */
export function eventOptionEnemies(eventId: string, zone?: ZoneId | null): Record<string, string> {
  // Package 4, part I: a story step's event (Route 9's toll) is in the book of events, not in a zone's pool.
  const ev = EVENT_BY_ID[eventId] ?? EXPEDITION_EVENTS.find((e) => e.id === eventId);
  const out: Record<string, string> = {};
  for (const o of ev?.options ?? []) {
    const f = o.effects.fight ?? o.fail?.fight ?? o.success?.fight;
    const name = f ? enemyArt(f.enemy, zone) : undefined;
    if (name) out[o.id] = name;
  }
  return out;
}

/** Package 4, part I: the characters of the story chains on their steps' events. */
const STORY_FACES: Record<string, string> = { story_patient07_1: CHARACTER_ART.doc_mara, story_patient07_6: CHARACTER_ART.doc_mara, story_off_the_grid_3: CHARACTER_ART.elias_crane };

export function eventArt(eventId: string, expeditionId: string, zone?: ZoneId | null): { folder: 'npc' | 'enemies'; name: string; option?: string } | null {
  if (STORY_FACES[eventId]) return { folder: 'npc', name: STORY_FACES[eventId] };
  const s = EVENT_STRANGERS[eventId];
  if (s) {
    const pool = s.gender ? NPC_PORTRAITS.filter((p) => p.endsWith(`_${s.gender}`)) : NPC_PORTRAITS;
    return { folder: 'npc', name: pool[hash(expeditionId + eventId) % pool.length], option: s.option };
  }
  if (EVENT_NAMED[eventId]) return { folder: 'enemies', name: NAMED_ART[EVENT_NAMED[eventId]] };
  const enemy = EVENT_ENEMIES[eventId];
  return enemy ? { folder: 'enemies', name: enemyArt(enemy, zone) ?? ENEMY_ART[enemy]! } : null;
}
